import { useEffect } from 'react';
import { useForm, useFieldArray, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCategoriesFlat } from '@/hooks/use-categories';
import { useCountryPacks } from '@/hooks/use-country-packs';
import { useTaxCategoriesForPacks } from '@/hooks/use-tax-rates';
import { useT } from '@/i18n/use-t';
import { gramsToMg } from '@/lib/item-weight';

// SSC-108: grams, up to 3 decimals, `.` or `,`; blank means no weight.
const WeightGrams = z.string().refine((value) => gramsToMg(value) !== undefined, {
  message: 'weightInvalid',
});

const Schema = z.object({
  sku: z.string().min(1).max(64),
  name: z.string().min(1).max(255),
  description: z.string().max(4096).optional().or(z.literal('')),
  categoryId: z.string().min(1, 'Pick a category'),
  unitOfMeasure: z.enum(['each', 'kg', 'g', 'l', 'ml']),
  taxCategory: z.string().min(1).max(32),
  ageRestriction: z.coerce.number().int().min(0).max(120),
  isVariableWeight: z.boolean(),
  skipBaggingAllowed: z.boolean(),
  // RL-439: stamps per one unit, 1..EXCISE_STAMP_COUNT_MAX (mirrors the API).
  isMarked: z.boolean(),
  exciseStampCount: z.coerce.number().int().min(1).max(99),
  weightCheckMode: z.enum(['presence', 'tolerance', 'disabled']),
  grossWeightGrams: WeightGrams,
  netWeightGrams: WeightGrams,
  status: z.enum(['active', 'archived']),
  barcodes: z.array(
    z.object({
      barcode: z.string().min(1, 'Required').max(64),
      isPrimary: z.boolean(),
    }),
  ),
  attributes: z.array(
    z.object({
      key: z.string().min(1, 'Required').max(64),
      value: z.string().max(1024),
    }),
  ),
});

export type ItemFormValues = z.infer<typeof Schema>;

/*
 * MICRO-43 P3: the drink → deposit link. The till reads it from the attribute
 * `deposit` = `<deposit item SKU>|<deposits per unit sold>` and refuses the
 * drink if it is malformed, so the form edits it in two fields of its own and
 * writes the attribute back. Same grammar as the contracts' DepositAttributeValue
 * (mirrored here: the admin UI imports contracts for types only).
 */
const DEPOSIT_KEY = 'deposit';
const DEPOSIT_SKU = /^[^|\s]{1,64}$/;
const DEPOSIT_UNITS = /^[1-9][0-9]{0,2}$/;
const DEPOSIT_VALUE = /^([^|\s]{1,64})\|([1-9][0-9]{0,2})$/;

type DepositFieldValues = { depositSku: string; depositUnits: string };
type Attribute = { key: string; value: string };

function checkDepositFields(values: DepositFieldValues, ctx: z.RefinementCtx): void {
  const sku = values.depositSku.trim();
  const units = values.depositUnits.trim();
  if (sku === '' && units === '') return;
  if (!DEPOSIT_SKU.test(sku)) {
    ctx.addIssue({ code: 'custom', path: ['depositSku'], message: 'depositSkuInvalid' });
  }
  if (!DEPOSIT_UNITS.test(units)) {
    ctx.addIssue({ code: 'custom', path: ['depositUnits'], message: 'depositUnitsInvalid' });
  }
}

/** Both empty (no deposit), or a deposit item SKU and 1-999 deposits per unit sold. */
export const DepositFields = z
  .object({ depositSku: z.string(), depositUnits: z.string() })
  .superRefine(checkDepositFields);

const FormSchema = Schema.extend({ depositSku: z.string(), depositUnits: z.string() }).superRefine(
  checkDepositFields,
);
type FormValues = ItemFormValues & DepositFieldValues;

/**
 * The stored `deposit` attribute → the form's fields. A value the till could
 * not read (or a second deposit) stays in the attribute list as it is, so the
 * operator sees it and can fix it.
 */
export function splitDepositAttribute(
  attributes: readonly Attribute[],
): { attributes: Attribute[] } & DepositFieldValues {
  const isDeposit = (a: Attribute) => a.key.toLowerCase() === DEPOSIT_KEY;
  const deposits = attributes.filter(isDeposit);
  const match = deposits.length === 1 ? DEPOSIT_VALUE.exec(deposits[0]?.value ?? '') : null;
  if (match === null) return { attributes: [...attributes], depositSku: '', depositUnits: '' };
  return {
    attributes: attributes.filter((a) => !isDeposit(a)),
    depositSku: match[1] ?? '',
    depositUnits: match[2] ?? '',
  };
}

/** The form's fields → the attribute list, with one `deposit` attribute or none. */
export function joinDepositAttribute(
  values: { attributes: readonly Attribute[] } & DepositFieldValues,
): Attribute[] {
  const sku = values.depositSku.trim();
  const units = values.depositUnits.trim();
  if (sku === '' && units === '') return [...values.attributes];
  return [...values.attributes, { key: DEPOSIT_KEY, value: `${sku}|${units}` }];
}

/** MICRO-43 P2a: the `sourceSystem` a GP-projected attribute carries. */
const GP_SOURCE = 'gp-compat';

/**
 * MICRO-43 P2b — the item's GP-owned `deposit` rows (GP wins) come out of the
 * editable attributes: the form shows them read-only and sends them back as
 * they are, whatever their value, so an edit never changes or drops them.
 */
export function splitGpDeposits(
  attributes: ReadonlyArray<{ key: string; value: string; sourceSystem?: string | null }>,
): { attributes: Attribute[]; gpDeposits: string[] } {
  const owned = (a: { key: string; sourceSystem?: string | null }) =>
    a.key.toLowerCase() === DEPOSIT_KEY && a.sourceSystem === GP_SOURCE;
  return {
    attributes: attributes.filter((a) => !owned(a)).map((a) => ({ key: a.key, value: a.value })),
    gpDeposits: attributes.filter(owned).map((a) => a.value),
  };
}

function toFormValues(values: ItemFormValues): FormValues {
  return { ...values, ...splitDepositAttribute(values.attributes) };
}

/**
 * SSC-108: the form's grams text → the API's milligrams. Only called on
 * values the schema has accepted, so an invalid entry cannot reach here.
 */
export function formWeightsToMg(
  values: Pick<ItemFormValues, 'grossWeightGrams' | 'netWeightGrams'>,
): {
  grossWeightMg: number | null;
  netWeightMg: number | null;
} {
  return {
    grossWeightMg: gramsToMg(values.grossWeightGrams) ?? null,
    netWeightMg: gramsToMg(values.netWeightGrams) ?? null,
  };
}

export function ItemForm({
  defaultValues,
  weightSource = null,
  barcodeAttributes = {},
  gpDeposits = [],
  submitLabel,
  onSubmit,
  busy,
  errorText,
}: {
  defaultValues: ItemFormValues;
  /** SSC-108: where the stored weights came from (`gp-compat`, `operator`). */
  weightSource?: string | null;
  barcodeAttributes?: Readonly<Record<string, readonly [string, string][]>>;
  /** MICRO-43 P2b: GP-owned deposit values, shown read-only and saved back unchanged. */
  gpDeposits?: readonly string[];
  submitLabel: string;
  onSubmit: (values: ItemFormValues) => Promise<void>;
  busy?: boolean;
  errorText?: string | null;
}) {
  const t = useT();
  const cats = useCategoriesFlat();
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: toFormValues(defaultValues),
  });
  // Re-seed when defaultValues change (i.e. detail loaded after first render).
  useEffect(() => {
    reset(toFormValues(defaultValues));
  }, [defaultValues, reset]);

  // RL-147: resolve the typed category against EVERY enabled country pack.
  //
  // Reading `packs.data[0]` presented one arbitrary pack's rate as the item's
  // rate. A deployment can have several enabled, and the same category id can
  // carry a different rate in each, so that was a wrong number shown
  // authoritatively.
  const packs = useCountryPacks({ enabled: true });
  const enabledPacks = packs.data ?? [];
  const packCategories = useTaxCategoriesForPacks(enabledPacks.map((pack) => pack.id));
  const taxCategoryValue = watch('taxCategory');
  const ratesByPack = enabledPacks.map((pack, index) => ({
    label: pack.country === '' ? pack.displayName : pack.country.toUpperCase(),
    categories: packCategories[index]?.data?.categories,
  }));

  const barcodes = useFieldArray({ control, name: 'barcodes' });
  const attributes = useFieldArray({ control, name: 'attributes' });
  const barcodeValues = watch('barcodes');
  const isMarked = watch('isMarked');

  const handler: SubmitHandler<FormValues> = async (values) => {
    const { depositSku, depositUnits, ...rest } = values;
    await onSubmit({
      ...rest,
      attributes:
        gpDeposits.length > 0
          ? [...rest.attributes, ...gpDeposits.map((value) => ({ key: DEPOSIT_KEY, value }))]
          : joinDepositAttribute({ attributes: rest.attributes, depositSku, depositUnits }),
    });
  };

  return (
    <form onSubmit={handleSubmit(handler)} className="space-y-6">
      <section className="grid gap-4 rounded-md border bg-background p-5 md:grid-cols-2">
        <Field label="SKU" error={errors.sku?.message}>
          <Input {...register('sku')} />
        </Field>
        <Field label={t('catalog.common.name', 'Name')} error={errors.name?.message}>
          <Input {...register('name')} />
        </Field>
        <Field label={t('catalog.common.category', 'Category')} error={errors.categoryId?.message}>
          <Select {...register('categoryId')}>
            <option value="">{t('catalog.common.selectOption', '— select —')}</option>
            {cats.flat.map((c) => (
              <option key={c.id} value={c.id}>
                {'— '.repeat(c.depth)}
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('catalog.itemForm.taxCategory', 'Tax category')}
          error={errors.taxCategory?.message}
          hint={taxRateHint(taxCategoryValue ?? '', ratesByPack, {
            noneConfigured: t(
              'catalog.itemForm.noRateConfigured',
              'No rate configured for this category',
            ),
            noneSet: t('catalog.itemForm.noRateSet', 'No rate set for this category yet'),
          })}
        >
          <Input {...register('taxCategory')} placeholder="standard" />
        </Field>
        <Field label={t('catalog.itemForm.unitOfMeasure', 'Unit of measure')}>
          <Select {...register('unitOfMeasure')}>
            <option value="each">each</option>
            <option value="kg">kg</option>
            <option value="g">g</option>
            <option value="l">l</option>
            <option value="ml">ml</option>
          </Select>
        </Field>
        <Field
          label={t('catalog.itemForm.ageRestriction', 'Age restriction')}
          error={errors.ageRestriction?.message}
        >
          <Input type="number" min={0} max={120} {...register('ageRestriction')} />
        </Field>
        <Field label={t('catalog.common.status', 'Status')}>
          <Select {...register('status')}>
            <option value="active">active</option>
            <option value="archived">archived</option>
          </Select>
        </Field>
        <Field label={t('catalog.itemForm.weightCheckMode', 'Weight check mode')}>
          <Select {...register('weightCheckMode')}>
            <option value="presence">presence</option>
            <option value="tolerance">tolerance</option>
            <option value="disabled">disabled</option>
          </Select>
        </Field>
        <Field
          label={t('catalog.itemForm.grossWeight', 'Gross weight (g)')}
          error={
            errors.grossWeightGrams === undefined
              ? undefined
              : t('catalog.itemForm.weightInvalid', 'Enter grams, up to 3 decimals, above 0')
          }
          hint={
            weightSource === 'gp-compat'
              ? t(
                  'catalog.itemForm.weightFromGp',
                  'From GP. A change here is kept and GP no longer updates it; clear both weights to take GP values again.',
                )
              : t('catalog.itemForm.weightHint', 'One base unit, in grams (up to 3 decimals)')
          }
        >
          <Input inputMode="decimal" autoComplete="off" {...register('grossWeightGrams')} />
        </Field>
        <Field
          label={t('catalog.itemForm.netWeight', 'Net weight (g)')}
          error={
            errors.netWeightGrams === undefined
              ? undefined
              : t('catalog.itemForm.weightInvalid', 'Enter grams, up to 3 decimals, above 0')
          }
        >
          <Input inputMode="decimal" autoComplete="off" {...register('netWeightGrams')} />
        </Field>
        <Field label="" inline>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...register('isVariableWeight')} />
            {t('catalog.itemForm.variableWeight', 'Variable-weight item')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...register('skipBaggingAllowed')} />
            {t('catalog.itemForm.skipBagging', 'Skip-bagging allowed')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              {...register('isMarked', {
                // RL-439: the count starts at 1 each time the flag is switched
                // on, and a hidden count can never block the save.
                onChange: () => setValue('exciseStampCount', 1),
              })}
            />
            {t('catalog.itemForm.isMarked', 'Excise stamp (marked product)')}
          </label>
        </Field>
        {isMarked && (
          <Field
            label={t('catalog.itemForm.exciseStampCount', 'Number of excise stamps')}
            error={errors.exciseStampCount?.message}
            hint={t(
              'catalog.itemForm.exciseStampCountHint',
              'Per one unit of the product. Each stamp is four letters and six digits (AAAA000000).',
            )}
          >
            <Input
              aria-label={t('catalog.itemForm.exciseStampCount', 'Number of excise stamps')}
              type="number"
              min={1}
              max={99}
              step={1}
              {...register('exciseStampCount')}
            />
          </Field>
        )}
        <Field label={t('catalog.itemForm.description', 'Description')} full>
          <Textarea rows={3} {...register('description')} />
        </Field>
      </section>

      <section className="rounded-md border bg-background p-5">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium">{t('catalog.itemForm.barcodes', 'Barcodes')}</h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              barcodes.append({ barcode: '', isPrimary: barcodes.fields.length === 0 })
            }
          >
            {t('catalog.itemForm.addBarcode', 'Add barcode')}
          </Button>
        </header>
        <div className="space-y-2">
          {barcodes.fields.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t('catalog.itemForm.noBarcodes', 'No barcodes yet.')}
            </p>
          )}
          {barcodes.fields.map((field, i) => {
            const barcode = barcodeValues[i]?.barcode ?? field.barcode;
            return (
              <div key={field.id} className="space-y-1">
                <div className="flex items-center gap-2">
                  <Input
                    className="font-mono"
                    placeholder="EAN / UPC"
                    {...register(`barcodes.${i}.barcode`)}
                  />
                  <label className="flex items-center gap-1 text-xs text-muted-foreground">
                    <input type="checkbox" {...register(`barcodes.${i}.isPrimary`)} />
                    {t('catalog.itemForm.primary', 'primary')}
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => barcodes.remove(i)}
                  >
                    ✕
                  </Button>
                </div>
                {(barcodeAttributes[barcode] ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-2 pl-1 text-xs text-muted-foreground">
                    {(barcodeAttributes[barcode] ?? []).map(([key, value]) => (
                      <span key={key} className="rounded bg-muted px-2 py-1">
                        {key}: {value}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-md border bg-background p-5">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium">{t('catalog.itemForm.attributes', 'Attributes')}</h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => attributes.append({ key: '', value: '' })}
          >
            {t('catalog.itemForm.addAttribute', 'Add attribute')}
          </Button>
        </header>
        <div className="space-y-2">
          {attributes.fields.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t('catalog.itemForm.noAttributes', 'No attributes yet.')}
            </p>
          )}
          {attributes.fields.map((field, i) => (
            <div key={field.id} className="flex items-center gap-2">
              <Input
                className="w-48"
                placeholder={t('catalog.itemForm.attributeKey', 'key')}
                {...register(`attributes.${i}.key`)}
              />
              <Input
                placeholder={t('catalog.itemForm.attributeValue', 'value')}
                {...register(`attributes.${i}.value`)}
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => attributes.remove(i)}>
                ✕
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-md border bg-background p-5">
        <header className="mb-3 space-y-1">
          <h2 className="text-sm font-medium">{t('catalog.itemForm.deposit', 'Deposit')}</h2>
          <p className="text-xs text-muted-foreground">
            {t(
              'catalog.itemForm.depositHint',
              'The container deposit the till adds with each unit sold. Leave both empty for none.',
            )}
          </p>
        </header>
        {gpDeposits.length > 0 ? (
          <div className="space-y-1 text-sm">
            {gpDeposits.map((value) => (
              <p key={value} className="font-mono">
                {value}
              </p>
            ))}
            <p className="text-xs text-muted-foreground">
              {t(
                'catalog.itemForm.depositFromGp',
                'Set by GP: change it in GP. It is saved back unchanged with the item.',
              )}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label={t('catalog.itemForm.depositSku', 'Deposit item SKU')}
              error={
                errors.depositSku === undefined
                  ? undefined
                  : t('catalog.itemForm.depositSkuInvalid', 'Enter the deposit item SKU')
              }
            >
              <Input {...register('depositSku')} />
            </Field>
            <Field
              label={t('catalog.itemForm.depositUnits', 'Deposits per unit sold')}
              error={
                errors.depositUnits === undefined
                  ? undefined
                  : t('catalog.itemForm.depositUnitsInvalid', 'A whole number from 1 to 999')
              }
            >
              <Input inputMode="numeric" {...register('depositUnits')} />
            </Field>
          </div>
        )}
      </section>

      {errorText !== null && errorText !== undefined && errorText !== '' && (
        <p className="text-sm text-destructive">{errorText}</p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={busy === true}>
          {busy === true ? t('common.saving', 'Saving…') : submitLabel}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  children,
  error,
  inline,
  full,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  error?: string | undefined;
  inline?: boolean;
  full?: boolean;
  hint?: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${full === true ? 'md:col-span-2' : ''}`}>
      {label !== '' && <Label>{label}</Label>}
      <div className={inline === true ? 'flex flex-wrap gap-4' : ''}>{children}</div>
      {error !== undefined && <p className="text-xs text-destructive">{error}</p>}
      {error === undefined && hint !== undefined && hint !== null && (
        <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

/**
 * RL-147: the percent charged for the category, resolved from the enabled
 * country pack.
 *
 * The letter alone does not tell an operator what a customer pays, and the two
 * live in different screens today — the category on the item, the rate under
 * Country packs.
 *
 * `null` means "say nothing": while the pack or its categories are still
 * loading there is no honest answer, and a blank is better than a wrong one.
 * A category the pack does not declare says so explicitly rather than showing
 * a rate it does not have — GP-projected items carry Ukrainian PRRO letters
 * (D/E/A) which are deliberately absent from the pack's catalogue, so this is
 * the common case on a GP-loaded tenant, not an edge case (see RL-43).
 *
 * `labels` carries the two prose answers in. The function is pure and has no
 * hook access, so the caller resolves them through `useT` and the English
 * defaults keep it usable (and testable) on its own.
 */
export function taxRateHint(
  taxCategory: string,
  packs: ReadonlyArray<{
    label: string;
    categories: ReadonlyArray<{ id: string; currentRate: number | null }> | undefined;
  }>,
  labels: { noneConfigured: string; noneSet: string } = {
    noneConfigured: 'No rate configured for this category',
    noneSet: 'No rate set for this category yet',
  },
): string | null {
  const trimmed = taxCategory.trim();
  if (trimmed === '' || packs.length === 0) return null;
  // Any pack still loading means there is no honest answer yet.
  if (packs.some((pack) => pack.categories === undefined)) return null;

  const resolved = packs.map((pack) => ({
    label: pack.label,
    match: pack.categories?.find((category) => category.id === trimmed),
  }));

  const declaring = resolved.filter((entry) => entry.match !== undefined);
  if (declaring.length === 0) return labels.noneConfigured;

  const withRate = declaring.filter((entry) => entry.match?.currentRate !== null);
  if (withRate.length === 0) return labels.noneSet;

  const distinct = new Set(withRate.map((entry) => entry.match?.currentRate));
  // Every pack that declares it agrees, so one number is the whole truth.
  if (distinct.size === 1) return `${withRate[0]?.match?.currentRate}%`;

  // They disagree. Name each pack rather than picking one and implying it is
  // authoritative — which is the defect this replaces.
  return withRate.map((entry) => `${entry.match?.currentRate}% (${entry.label})`).join(' · ');
}
